import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { StatusBadge } from "@/components/StatusBadge.js";

interface ProjectSummary {
  id: string;
  name: string;
  status: string;
  address: string;
  buildingType: string;
  createdAt: string;
  updatedAt: string;
}

interface CustomerDetail {
  id: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  type: string;
  createdAt: string;
  projects: ProjectSummary[];
}

const TYPE_LABELS: Record<string, string> = {
  private: "Privat",
  commercial: "Gewerbe",
  industrial: "Industrie",
  agricultural: "Landwirtschaft",
};

const BUILDING_TYPE_LABELS: Record<string, string> = {
  residential: "Wohngebäude",
  commercial: "Gewerbe",
  industrial: "Industrie",
  agricultural: "Landwirtschaft",
  mixed: "Gemischt",
};

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
    type: "private",
  });

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.get<CustomerDetail>(`/customers/${id}`);
    if (res.success) {
      setCustomer(res.data);
    } else {
      setError("Kunde konnte nicht geladen werden.");
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, [id]);

  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name,
        contactEmail: customer.contactEmail ?? "",
        contactPhone: customer.contactPhone ?? "",
        address: customer.address ?? "",
        type: customer.type,
      });
    }
  }, [customer]);

  const set = (field: keyof typeof form, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    const res = await apiClient.patch<CustomerDetail>(`/customers/${id}`, {
      name: form.name.trim(),
      type: form.type,
      contactEmail: form.contactEmail.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      address: form.address.trim() || null,
    });
    if (res.success) {
      setCustomer(res.data);
      setEditing(false);
    } else {
      setSaveError(
        (res as { success: false; error: { message: string } }).error.message,
      );
    }
    setSaving(false);
  };

  const handleCancel = () => {
    if (customer) {
      setForm({
        name: customer.name,
        contactEmail: customer.contactEmail ?? "",
        contactPhone: customer.contactPhone ?? "",
        address: customer.address ?? "",
        type: customer.type,
      });
    }
    setEditing(false);
    setSaveError(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 64, color: "var(--color-text-muted)" }}>
        Lade Kunde…
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ color: "var(--color-error)", marginBottom: 16 }}>
          {error ?? "Kunde nicht gefunden."}
        </div>
        <Link to="/customers" className="btn-secondary" style={{ textDecoration: "none" }}>
          ← Zurück zu Kunden
        </Link>
      </div>
    );
  }

  const activeProjects = customer.projects.filter((p) => p.status !== "archived");
  const lastProject = customer.projects[0];

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link
            to="/customers"
            style={{ color: "var(--color-text-muted)", textDecoration: "none", fontSize: 14 }}
          >
            ← Kunden
          </Link>
          <span style={{ color: "var(--color-text-muted)" }}>/</span>
          <h1 className="page-title" style={{ margin: 0 }}>{customer.name}</h1>
          <span className="badge badge-draft">{TYPE_LABELS[customer.type] ?? customer.type}</span>
        </div>
        {!editing && (
          <button className="btn-secondary" onClick={() => setEditing(true)}>
            Bearbeiten
          </button>
        )}
      </div>

      {/* KPI-Zeile */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: "16px 20px" }}>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
            Projekte gesamt
          </div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{customer.projects.length}</div>
        </div>
        <div className="card" style={{ padding: "16px 20px" }}>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
            Aktive Projekte
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--color-success, #16a34a)" }}>
            {activeProjects.length}
          </div>
        </div>
        <div className="card" style={{ padding: "16px 20px" }}>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
            Letztes Projekt
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {lastProject ? (
              <Link to={`/projects/${lastProject.id}`} style={{ color: "inherit" }}>
                {lastProject.name}
              </Link>
            ) : (
              <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>—</span>
            )}
          </div>
          {lastProject && (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>
              {new Date(lastProject.createdAt).toLocaleDateString("de-AT")}
            </div>
          )}
        </div>
      </div>

      {/* Stammdaten-Karte */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Stammdaten</h2>
          {editing && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" onClick={handleCancel} disabled={saving}>
                Abbrechen
              </button>
              <button
                className="btn-primary"
                onClick={() => void handleSave()}
                disabled={saving || !form.name.trim()}
              >
                {saving ? "Speichern…" : "Speichern"}
              </button>
            </div>
          )}
        </div>

        {saveError && (
          <div style={{ color: "var(--color-error)", marginBottom: 12, fontSize: 13 }}>
            {saveError}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              Name *
            </label>
            {editing ? (
              <input value={form.name} onChange={(e) => set("name", e.target.value)} />
            ) : (
              <div style={{ fontWeight: 500 }}>{customer.name}</div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              Typ
            </label>
            {editing ? (
              <select value={form.type} onChange={(e) => set("type", e.target.value)}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            ) : (
              <div>{TYPE_LABELS[customer.type] ?? customer.type}</div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              E-Mail
            </label>
            {editing ? (
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
                placeholder="beispiel@domain.at"
              />
            ) : (
              <div style={{ color: customer.contactEmail ? "inherit" : "var(--color-text-muted)" }}>
                {customer.contactEmail ?? "—"}
              </div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              Telefon
            </label>
            {editing ? (
              <input
                value={form.contactPhone}
                onChange={(e) => set("contactPhone", e.target.value)}
                placeholder="+43 512 123456"
              />
            ) : (
              <div style={{ color: customer.contactPhone ? "inherit" : "var(--color-text-muted)" }}>
                {customer.contactPhone ?? "—"}
              </div>
            )}
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              Adresse
            </label>
            {editing ? (
              <input
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="Musterstraße 1, 6020 Innsbruck"
              />
            ) : (
              <div style={{ color: customer.address ? "inherit" : "var(--color-text-muted)" }}>
                {customer.address ?? "—"}
              </div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4 }}>
              Angelegt am
            </label>
            <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
              {new Date(customer.createdAt).toLocaleDateString("de-AT")}
            </div>
          </div>
        </div>
      </div>

      {/* Projektliste */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
            Projekte ({customer.projects.length})
          </h2>
          <Link
            to={`/projects/new?customerId=${customer.id}`}
            className="btn-primary"
            data-testid="customer-new-project"
            style={{ textDecoration: "none", fontSize: 13 }}
          >
            + Neues Projekt
          </Link>
        </div>
        {customer.projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: "var(--color-text-muted)" }}>
            Noch keine Projekte für diesen Kunden.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                {["Name", "Status", "Adresse", "Gebäudetyp", "Erstellt am"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "var(--color-text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customer.projects.map((p, i) => (
                <tr
                  key={p.id}
                  style={{
                    borderBottom: "1px solid var(--color-border)",
                    background: i % 2 === 0 ? "transparent" : "#fafafa",
                  }}
                >
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>
                    <Link
                      to={`/projects/${p.id}`}
                      style={{ color: "var(--color-primary)", textDecoration: "none" }}
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <StatusBadge status={p.status} />
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>
                    {p.address || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>
                    {BUILDING_TYPE_LABELS[p.buildingType] ?? p.buildingType}
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>
                    {new Date(p.createdAt).toLocaleDateString("de-AT")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
